import React from "react";
import styled, { css } from "react-emotion";
import { ColorPicker, Image, SelectOptions, Input } from "components";
import { FlexRow, H1, flex } from "appStyles";
import { withRedux } from "helpers";
import Tooltip from "@material-ui/core/Tooltip";
import HelpTwoToneIcon from "@material-ui/icons/HelpTwoTone";
import { makeStyles } from "@material-ui/core/styles";

const columnsStyle = css`
  display: flex;
  align-items: center;
  margin-top: 2em;
`;
const P = styled("p")`
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #000000;
  display: block;
  font-family: "Lucida Grande", "Lucida Sans Unicode", "Lucida Sans", Geneva,
    Verdana, sans-serif;
  font-size: 0.9375rem;
  line-height: 0.9375rem;
  display: flex;
  align-items: flex-end;
  letter-spacing: 0.4px;
  width: 215px;
`;

const columnOption = css`
  ${flex};
  width: 2.6rem;
  height: 2.6rem;
  border: 1px solid aqua;
  cursor: pointer;
  font-size: 0.7rem;
  transition: 0.2s;

  &:hover {
    background: rgba(200, 200, 200, 0.3);
    transition: 0.3s;
  }
`;

const columnOptionContainer = css`
  display: flex;
  flex-direction: row;
  margin-left: 1.5em;
`;

const imagesPerRowText = `Your images will automatically crop and scale to the space available. While your image might be cut off in the preview, the user may click to view the whole image.`;

const useStyles = makeStyles((theme) => ({
  customWidth: {
    maxWidth: 300,
    fontSize: "14px",
  },
}));

const EditBackground = (props) => {
  const {
    backgroundColor,
    backgroundImage,
    croppingAspectRatio,
    update,
    activeTemplate: { pageName },
  } = props;

  const classes = useStyles();

  const updateBackgroundImage = (url) => {
    props.updateBackgroundImage(url);
  };

  const updateBackgroundColor = (color) => {
    props.updateBackgroundColor(color);
  };

  const updatePageName = (pageName) => {
    const updatedTemplate = {
      ...props.activeTemplate,
      pageName,
    };
    props.actions.updateTemplate(updatedTemplate);
  };

  const setColumns = (idx) => () => {
    const updatedComponent = {
      ...props.pictureCatalogComponent,
    };
    const columnCount = idx + 1;
    updatedComponent.view.columns = columnCount;
    props.updateComponent(updatedComponent);
  };

  const setSectionDisplayType = (sectionDisplayType) => {
    const updatedComponent = {
      ...props.pictureCatalogComponent,
    };
    updatedComponent.view.sectionDisplayType = sectionDisplayType;
    props.updateComponent(updatedComponent);
  };

  const sectionDisplayTypeOptions = () => {
    const { sectionDisplayType } = props.pictureCatalogComponent.view;
    if (sectionDisplayType != "fullPage") {
      setSectionDisplayType("fullPage");
    }
    return null;
    const options = [
      {
        value: "fullPage",
        label: "All on One Page",
      },
      {
        value: "tableOfContents",
        label: "Table of Contents (Menu)",
      },
    ];
    return (
      <div className={columnsStyle}>
        Choose a menu type:
        <div className={columnOptionContainer}>
          <SelectOptions
            width="15em"
            setValue={setSectionDisplayType}
            options={options}
            value={sectionDisplayType}
          />
        </div>
      </div>
    );
  };

  const sectionComponentOptions = () => {
    const columns = props.pictureCatalogComponent.view.columns;
    return (
      <div className={columnsStyle}>
        Images Per Row
        <Tooltip
          title={imagesPerRowText}
          classes={{ tooltip: classes.customWidth }}
        >
          <HelpTwoToneIcon
            color="primary"
            fontSize="small"
            style={{ marginLeft: "15px" }}
          />
        </Tooltip>
        <div className={columnOptionContainer}>
          {[...Array(3)].map((_, idx) => {
            const isActive = columns === idx + 1;
            return (
              <div
                className={columnOption}
                onClick={setColumns(idx)}
                style={{
                  background: isActive ? "rgba(200,200,200,.4)" : "",
                }}
                key={idx}
              >
                {idx + 1}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const aspectRatioOptions = () => {
    const options = [
      {
        value: "1.78",
        label: "Landscape",
      },
      {
        value: "0.8",
        label: "Portrait",
      },
      {
        value: "1.0",
        label: "Square",
      },
      {
        value: null,
        label: "Custom",
      },
    ];

    if (croppingAspectRatio != null) {
      // console.log("cropping ratio is not null");
      update(null);
    }
    return null;

    return (
      <div className={columnsStyle}>
        Choose an aspect ratio:
        <div className={columnOptionContainer}>
          <SelectOptions
            width="9em"
            value={croppingAspectRatio}
            setValue={update("croppingAspectRatio")}
            options={options}
          />
        </div>
      </div>
    );
  };

  const normalEditBackgroundComponent = () => {
    return (
      <div>
        <H1
          fontSize="24px"
          className="heading"
          lineHeight="35px"
          style={{
            letterSpacing: "2px",
            textAlign: "left",
          }}
        >
          Page Settings
        </H1>
        <FlexRow
          style={{ paddingTop: "20px", marginBottom: "20px" }}
          // height="auto"
          height="auto"
        >
          <P>Page Name</P>
          <Input
            field="Enter Page Name"
            defaultValue={pageName}
            onChange={updatePageName}
            type="text"
            style={{ width: "90%", marginTop: "0px" }}
            marginBottom={"0px"}
          />
        </FlexRow>
        <ColorPicker
          width="45%"
          title="Background Color"
          color={backgroundColor}
          onChange={updateBackgroundColor}
          marginBottom={10}
          marginLeft={"-25px"}
        />
        <div style={{ marginTop: "15px" }}>
          <Image
            options={{ croppingAspectRatio: null }}
            title="Background Image"
            imageUrl={backgroundImage && backgroundImage.url}
            uploadImage={updateBackgroundImage}
            titleWidth={210}
          />
        </div>
        {props.pictureCatalogComponent && sectionComponentOptions()}
        {props.pictureCatalogComponent && aspectRatioOptions()}
        {props.pictureCatalogComponent && sectionDisplayTypeOptions()}
        {props.additionalPageSettings}
      </div>
    );
  };

  switch (props.templateType) {
    default:
      return normalEditBackgroundComponent();
  }
};

export default withRedux(EditBackground);
